import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Shield, Timer } from 'lucide-react';
import SuperAdminShell from './SuperAdminShell';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { fetchSystemSettings, patchSystemSettings } from '../../lib/adminCatalogApi';

const PRESETS = [7, 14, 30, 60, 90];

export default function SuperAdminPlatformControlPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [graceDays, setGraceDays] = useState(14);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSystemSettings()
      .then((data) => {
        if (cancelled) return;
        setSettings(data);
        setGraceDays(Number(data.account_deletion_grace_days) || 14);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load platform control settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    const days = Number(graceDays);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error('Grace period must be between 1 and 365 days');
      return;
    }
    setSaving(true);
    try {
      const data = await patchSystemSettings({ account_deletion_grace_days: Math.round(days) });
      setSettings(data);
      setGraceDays(Number(data.account_deletion_grace_days) || days);
      toast.success('Platform control settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SuperAdminShell
      title="Platform Control"
      description="Operator controls for account lifecycle, retention, and platform-wide policies."
      testId="super-admin-platform-control"
    >
      <div className="space-y-6 max-w-2xl">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] text-base flex items-center gap-2">
              <Timer className="w-5 h-5" />
              Account deletion grace period
            </CardTitle>
            <CardDescription>
              When a user requests account deletion, the account stays inactive for this many days
              before it is permanently deleted (workspaces they solely own are removed then too).
              Admins can still hard-delete immediately from Users.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 py-6">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((d) => (
                    <Button
                      key={d}
                      type="button"
                      size="sm"
                      variant={Number(graceDays) === d ? 'default' : 'outline'}
                      onClick={() => setGraceDays(d)}
                    >
                      {d} days
                    </Button>
                  ))}
                </div>
                <div className="space-y-2 max-w-xs">
                  <Label htmlFor="grace-days">Custom days (1–365)</Label>
                  <Input
                    id="grace-days"
                    type="number"
                    min={1}
                    max={365}
                    value={graceDays}
                    onChange={(e) => setGraceDays(e.target.value)}
                    data-testid="account-deletion-grace-days"
                  />
                </div>
                <p className="text-xs text-slate-500 flex items-start gap-2">
                  <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Current saved value:{' '}
                  <strong>{settings?.account_deletion_grace_days ?? 14}</strong> days. Permanent
                  deletion runs with the daily billing cron.
                </p>
                <Button onClick={save} disabled={saving} data-testid="platform-control-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminShell>
  );
}
